import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Clock, FileArrowDown, XCircle } from '@phosphor-icons/react';
import { API } from '../lib/api';
import { useSEO } from '../lib/seo';
import { useAuthUser } from '../lib/useAuth';
import { DashboardShell, EmptyState, Panel, StatCard, StatusPill, formatDate, inputCls } from '../components/dashboard/Bits';

const BuyerDashboard = () => {
  useSEO({ title: 'Buyer Dashboard | Vox Meridian', description: '', path: '/dashboard/buyer', robots: 'noindex, nofollow' });
  const auth = useAuthUser();
  const [rfqs, setRfqs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    if (auth.status !== 'signed-in') return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/buyer/rfqs`, { withCredentials: true });
      setRfqs(data.rfqs || []);
      const first = data.rfqs?.[0]?.id || null;
      setSelectedId((current) => current || first);
    } catch {
      toast.error('Could not load your RFQs.');
    } finally {
      setLoading(false);
    }
  }, [auth.status]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedId || auth.status !== 'signed-in') return;
    let active = true;
    axios
      .get(`${API}/buyer/rfqs/${selectedId}`, { withCredentials: true })
      .then(({ data }) => active && setDetail(data))
      .catch(() => active && setDetail(null));
    return () => { active = false; };
  }, [selectedId, auth.status]);

  const selected = useMemo(() => rfqs.find((r) => r.id === selectedId) || null, [rfqs, selectedId]);
  const quotes = detail?.quotes || selected?.quotes || [];

  const decide = async (quoteId, action) => {
    setActing(true);
    try {
      await axios.post(`${API}/buyer/quotes/${quoteId}/decision`, { action }, { withCredentials: true });
      toast.success(action === 'accept' ? 'Quote accepted.' : 'Quote declined.');
      await load();
      if (selectedId) {
        const { data } = await axios.get(`${API}/buyer/rfqs/${selectedId}`, { withCredentials: true });
        setDetail(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Decision failed.');
    } finally {
      setActing(false);
    }
  };

  const complete = async () => {
    if (!selectedId) return;
    setActing(true);
    try {
      await axios.post(`${API}/buyer/rfqs/${selectedId}/complete`, {}, { withCredentials: true });
      toast.success('Marked as completed.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not mark completed.');
    } finally {
      setActing(false);
    }
  };

  if (auth.status === 'checking') return <div className="flex min-h-[70vh] items-center justify-center bg-ink" data-testid="buyer-dashboard-loading"><p className="mono-label text-steel">Checking your session…</p></div>;
  if (auth.status === 'signed-out') {
    return <DashboardShell eyebrow="Buyer dashboard" title={<>Sign in first.</>} sub="Your RFQs and quotes are private to your Google account."><EmptyState testId="buyer-signed-out" title="Google sign-in required." text="Sign in with the same email you used on your RFQ." actionLabel="Go to sign in" actionTo="/account" /></DashboardShell>;
  }

  const waiting = rfqs.reduce((sum, r) => sum + (r.quotes_waiting || 0), 0);
  const awarded = rfqs.filter((r) => r.status === 'awarded').length;
  const completed = rfqs.filter((r) => r.status === 'completed').length;

  return (
    <DashboardShell
      eyebrow="Buyer dashboard"
      title={<>Track RFQs. Compare quotes.</>}
      sub="Every RFQ submitted with this Google email appears here, with founder-reviewed status and quote decisions."
      actions={<div className="flex gap-3"><Link to="/buyers" data-testid="buyer-new-rfq" className="bg-volt px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink">Post another RFQ</Link><Link to="/dashboard/services" data-testid="buyer-open-services" className="border border-line px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-parchment hover:border-volt hover:text-volt">Vox Service Manager</Link></div>}
    >
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="buyer-dashboard-stats">
        <StatCard label="RFQs" value={rfqs.length} testId="buyer-stat-rfqs" />
        <StatCard label="Quotes waiting" value={waiting} testId="buyer-stat-waiting" />
        <StatCard label="Awarded" value={awarded} testId="buyer-stat-awarded" />
        <StatCard label="Completed" value={completed} testId="buyer-stat-completed" />
      </div>

      {!loading && rfqs.length === 0 ? (
        <EmptyState testId="buyer-empty" title="No RFQs linked yet." text="Submit an RFQ with this same email and it will appear here automatically." actionLabel="Post an RFQ" actionTo="/buyers" />
      ) : (
        <div className="grid gap-6 lg:grid-cols-12">
          <Panel title="My RFQs" testId="buyer-rfq-list" className="lg:col-span-4">
            <div className="space-y-3">
              {rfqs.map((rfq) => (
                <button
                  type="button"
                  key={rfq.id}
                  data-testid={`buyer-rfq-${rfq.id}`}
                  onClick={() => setSelectedId(rfq.id)}
                  className={`w-full border p-4 text-left transition-colors duration-200 ${selectedId === rfq.id ? 'border-volt bg-volt/5' : 'border-line bg-ink hover:border-steel/50'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-sm font-bold text-parchment">{rfq.category}</p>
                      <p className="mt-1 text-xs text-steel">{rfq.country_name} · {rfq.budget_range}</p>
                    </div>
                    <StatusPill status={rfq.status} />
                  </div>
                  <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-steel">{rfq.scope}</p>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-steel/70">Deadline {formatDate(rfq.deadline)} · {rfq.quotes?.length || 0} quotes</p>
                </button>
              ))}
            </div>
          </Panel>

          <div className="lg:col-span-8">
            {!selected ? (
              <EmptyState testId="buyer-no-selection" title="Select an RFQ." text="Choose an RFQ from the list to review status, files and quotations." />
            ) : (
              <Panel testId="buyer-rfq-detail" title={selected.category}>
                <div className="flex flex-wrap items-center gap-3">
                  <StatusPill status={selected.status} />
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-steel">{selected.country_name} · {selected.delivery_mode?.replace(/_/g, ' ') || 'on site'}</span>
                  {selected.file?.storage_path && (
                    <a href={`${API}/files/rfq/${selected.id}`} data-testid="buyer-rfq-file" className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-volt">
                      <FileArrowDown size={14} /> {selected.file.original_filename}
                    </a>
                  )}
                </div>
                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  <div className="border border-line bg-ink p-4"><p className="mono-label text-steel">Budget</p><p className="mt-2 text-sm text-parchment">{selected.budget_range}</p></div>
                  <div className="border border-line bg-ink p-4"><p className="mono-label text-steel">Deadline</p><p className="mt-2 text-sm text-parchment">{formatDate(selected.deadline)}</p></div>
                  <div className="border border-line bg-ink p-4"><p className="mono-label text-steel">Matched</p><p className="mt-2 text-sm text-parchment">{selected.matched_count || 0} providers</p></div>
                </div>
                <div className="mt-6 border border-line bg-ink p-5">
                  <p className="mono-label text-steel">Scope</p>
                  <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-parchment">{selected.scope}</p>
                </div>
                {selected.status === 'awarded' && (
                  <button type="button" onClick={complete} disabled={acting} data-testid="buyer-mark-complete" className="mt-6 border border-volt px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-volt transition-colors duration-200 hover:bg-volt hover:text-ink disabled:opacity-50">
                    Mark job completed
                  </button>
                )}

                <div className="mt-8">
                  <h3 className="font-display text-lg font-black text-parchment">Quotations</h3>
                  {quotes.length === 0 ? (
                    <p className="mt-4 border border-line bg-ink p-5 text-sm text-steel" data-testid="buyer-no-quotes">No quotes yet. Matched providers have 24–48 hours to respond, subject to availability.</p>
                  ) : (
                    <div className="mt-4 space-y-4" data-testid="buyer-quote-list">
                      {quotes.map((quote) => (
                        <div key={quote.id} className="border border-line bg-ink p-5" data-testid={`buyer-quote-${quote.id}`}>
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="font-display text-base font-bold text-parchment">{quote.provider_company}</p>
                              <p className="mt-1 text-xs text-steel">{quote.provider_email}</p>
                            </div>
                            <StatusPill status={quote.status} />
                          </div>
                          <div className="mt-4 grid gap-3 sm:grid-cols-3">
                            <div><p className="mono-label text-steel">Amount</p><p className="mt-1 text-sm text-volt">{quote.currency} {quote.amount}</p></div>
                            <div><p className="mono-label text-steel">Lead time</p><p className="mt-1 text-sm text-parchment">{quote.lead_time}</p></div>
                            <div><p className="mono-label text-steel">Validity</p><p className="mt-1 text-sm text-parchment">{quote.validity || '—'}</p></div>
                          </div>
                          <div className="mt-4 grid gap-4 sm:grid-cols-2">
                            <div><p className="mono-label text-steel">Included</p><p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-parchment">{quote.inclusions}</p></div>
                            <div><p className="mono-label text-steel">Excluded</p><p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-steel">{quote.exclusions || '—'}</p></div>
                          </div>
                          {quote.notes && <p className="mt-4 text-sm leading-relaxed text-steel">{quote.notes}</p>}
                          {quote.file?.storage_path && (
                            <a href={`${API}/files/quote/${quote.id}`} data-testid={`buyer-quote-file-${quote.id}`} className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-volt">
                              <FileArrowDown size={14} /> {quote.file.original_filename}
                            </a>
                          )}
                          {['submitted', 'under_review'].includes(quote.status) && (
                            <div className="mt-5 flex flex-wrap gap-3">
                              <button type="button" disabled={acting} onClick={() => decide(quote.id, 'accept')} data-testid={`quote-accept-${quote.id}`} className="flex items-center gap-2 bg-volt px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink disabled:opacity-50"><CheckCircle size={15} /> Accept</button>
                              <button type="button" disabled={acting} onClick={() => decide(quote.id, 'decline')} data-testid={`quote-decline-${quote.id}`} className="flex items-center gap-2 border border-line px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-steel hover:border-volt hover:text-volt disabled:opacity-50"><XCircle size={15} /> Decline</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Panel>
            )}
          </div>
        </div>
      )}
    </DashboardShell>
  );
};

export default BuyerDashboard;
